const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);

    display: block;
    font-family: var(--ctl-font);
  }

  .empty{
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: var(--padding, var(--ctl-space-7) var(--ctl-space-5));
    min-height: var(--min-height, calc(var(--ctl-space-7) * 6.25));
  }

  /* bordered variant */
  :host([bordered]) .empty{
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-14);
    background: var(--ctl-color-surface);
  }

  .icon{
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--icon-size, calc(var(--ctl-space-7) + var(--ctl-space-4)));
    height: var(--icon-size, calc(var(--ctl-space-7) + var(--ctl-space-4)));
    margin-bottom: var(--ctl-space-5);
    color: var(--ctl-color-muted);
    opacity: var(--ctl-opacity-disabled);
  }

  .icon:empty{
    display: none;
  }

  .icon ::slotted(*){
    width: 100%;
    height: 100%;
  }

  .heading{
    font-size: var(--ctl-font-size-xl);
    font-weight: var(--ctl-font-weight-bold);
    color: var(--ctl-color-text);
    margin: 0 0 var(--ctl-space-2) 0;
    line-height: var(--ctl-leading-normal);
  }

  .heading:empty{
    display: none;
  }

  .description{
    font-size: var(--ctl-font-size-base);
    font-weight: var(--ctl-font-weight-normal);
    color: var(--ctl-color-muted);
    line-height: var(--ctl-leading-normal);
    margin: 0 0 var(--ctl-space-5) 0;
    max-width: 400px;
  }

  .description:empty{
    display: none;
  }

  .actions{
    display: flex;
    gap: var(--ctl-space-3);
    flex-wrap: wrap;
    justify-content: center;
  }

  .actions:empty{
    display: none;
  }

  /* sizes */
  :host([size="sm"]){
    --padding: var(--ctl-space-6) var(--ctl-space-4);
    --min-height: calc(var(--ctl-space-7) * 4.17);
    --icon-size: var(--ctl-space-7);
  }

  :host([size="sm"]) .heading{
    font-size: var(--ctl-font-size-lg);
  }

  :host([size="sm"]) .description{
    font-size: var(--ctl-font-size-md);
  }

  :host(:not([size]), [size="md"]){
    --padding: var(--ctl-space-7) var(--ctl-space-5);
    --min-height: calc(var(--ctl-space-7) * 6.25);
    --icon-size: calc(var(--ctl-space-7) + var(--ctl-space-4));
  }

  :host([size="lg"]){
    --padding: calc(var(--ctl-space-7) + var(--ctl-space-4)) var(--ctl-space-6);
    --min-height: calc(var(--ctl-space-7) * 8.33);
    --icon-size: calc(var(--ctl-space-7) + var(--ctl-space-6));
  }

  :host([size="lg"]) .heading{
    font-size: var(--ctl-space-5);
  }

  :host([size="lg"]) .description{
    font-size: var(--ctl-font-size-lg);
  }
</style>

<div class="empty" part="empty">
  <div class="icon" part="icon">
    <slot name="icon"></slot>
  </div>
  <h3 class="heading" part="heading">
    <slot name="heading"></slot>
  </h3>
  <p class="description" part="description">
    <slot name="description"></slot>
  </p>
  <div class="actions" part="actions">
    <slot name="actions"></slot>
  </div>
</div>
`;

export class CtlEmptyState extends HTMLElement {
  static get observedAttributes() {
    return ["size", "bordered"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
  }

  connectedCallback() {
    // defaults
    if (!this.hasAttribute("size")) this.setAttribute("size", "md");
    this._normalize();
  }

  attributeChangedCallback() {
    this._normalize();
  }

  // attrs
  get size() { return this.getAttribute("size") || "md"; }
  set size(v) { this.setAttribute("size", String(v || "md")); }

  get bordered() { return this.hasAttribute("bordered"); }
  set bordered(v) { v ? this.setAttribute("bordered", "") : this.removeAttribute("bordered"); }

  _normalize() {
    const size = this.size;
    if (!["sm", "md", "lg"].includes(size)) {
      this.setAttribute("size", "md");
    }
  }
}

customElements.define("ctl-empty-state", CtlEmptyState);
